import { Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { OSStep } from "@/molecules/OSStep"
import {
  DeployTab,
  TitleStepDeployTab,
  type CommonDeployTabProps
} from "@/pages/DeploymentsPage/components/molecules/DeployTab"
import { DeploymentKeyButton } from "@/pages/DeploymentsPage/components/molecules/DeploymentKeyButton"
import { DownloadStep } from "@/pages/DeploymentsPage/components/organisms/DownloadStep"
import {
  ScriptDeployCommand,
  type ScriptDeployCommandProps
} from "@/pages/DeploymentsPage/components/organisms/ScriptDeployCommand"
import {
  LAMBDA_ARCHITECTURES,
  LAMBDA_SIDE_SCAN_CF_FILENAME,
  LAMBDA_SIDE_SCAN_ONBOARDING_TEMPLATE_URL
} from "@/pages/DeploymentsPage/const"
import { getLambdaFiles } from "@/pages/DeploymentsPage/helpers/file"
import {
  CONFIGURE_GROUPS,
  CONFIGURE_LINES,
  getConfigureDefaultValues,
  getLambdaArchitecture,
  getPublishDefaultValues,
  getPublishLines,
  PUBLISH_GROUPS
} from "./helpers"

// The deployment key input gets the generate-key button
const CONFIGURE_LINE_BUTTONS: ScriptDeployCommandProps<
  keyof typeof CONFIGURE_LINES
>["lineButtons"] = { deploymentKey: DeploymentKeyButton }

export const LambdaTab = (props: CommonDeployTabProps) => {
  const { noError, version } = props
  const { t } = useTranslation("translation", { keyPrefix: "deployments" })
  const { t: tGeneral } = useTranslation("translation", {
    keyPrefix: "general"
  })

  // The function's architecture is selected first, like the VM / EC2 OS
  const {
    items,
    isWindows: _isWindows,
    ...rest
  } = OSStep({
    options: LAMBDA_ARCHITECTURES.map(({ platform, lambdaArch }) => ({
      label: t(`lambda.architecture.${lambdaArch}`),
      value: platform
    })),
    placeholder: t("lambda.selectArchitecture.placeholder"),
    waitingTitle: t("lambda.selectArchitecture.waiting"),
    getSteps: ({ OSPlatform }) => {
      const architecture = getLambdaArchitecture(OSPlatform)

      return [
        {
          title: (
            <DownloadStep
              noError={noError}
              files={[
                {
                  name: LAMBDA_SIDE_SCAN_CF_FILENAME,
                  contentEndpoint: LAMBDA_SIDE_SCAN_ONBOARDING_TEMPLATE_URL
                }
              ]}
              titlePrefix={tGeneral("deploy")}
              title={t("lambda.cloudFormationStep")}
            />
          ),
          status: "process"
        },
        {
          title: (
            <DownloadStep
              noError={noError}
              files={getLambdaFiles({
                version,
                packageSpecArch: architecture.packageSpecArch
              })}
              titlePrefix={tGeneral("download")}
              title={t("lambda.downloadStep")}
            />
          ),
          status: "process"
        },
        {
          title: (
            <TitleStepDeployTab prefix={t("publish")}>
              {`- ${t("lambda.publishStep")}`}
            </TitleStepDeployTab>
          ),
          description: (
            <ScriptDeployCommand
              lines={getPublishLines(version, architecture)}
              groups={PUBLISH_GROUPS}
              getDefaultValues={getPublishDefaultValues}
              {...props}
            />
          ),
          status: "process"
        },
        {
          title: (
            <TitleStepDeployTab prefix={tGeneral("install")}>
              {`- ${t("lambda.commandStep")}`}
            </TitleStepDeployTab>
          ),
          description: (
            <ScriptDeployCommand
              lines={CONFIGURE_LINES}
              groups={CONFIGURE_GROUPS}
              lineButtons={CONFIGURE_LINE_BUTTONS}
              getDefaultValues={getConfigureDefaultValues}
              {...props}
            />
          ),
          status: "process"
        }
      ]
    },
    getMessage: () => (
      <span>
        <Text>
          {t("lambda.alert.zipOnly")}
          <br />
          {t("kubernetes.alert.text")}
        </Text>
        <br />
        <b>{t("kubernetes.alert.note")}</b>
      </span>
    )
  })

  return <DeployTab steps={items} {...rest} />
}
